import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

// Import Lucide React icons
import { 
  List,
  Calendar,
  Grid,
  ChevronLeft,
  ChevronRight,
  Plus,
  Filter
} from 'lucide-react';

// Import our component views
import CalendarView from './CalendarView';
import ListView from './ListView';
import GridView from './GridView ';
import TrainingDetailsModal from './TrainingDetailsModal';

const Dashboard = ({ userRole, selectedScheme: initialSelectedScheme = null, selectedInstitute: initialSelectedInstitute = null }) => {
  // Dashboard state - default to list view as requested
  const [viewMode, setViewMode] = useState('list');
  const [currentDate, setCurrentDate] = useState(new Date());
  const navigate = useNavigate();
  const location = useLocation();
  
  // View and filter state
  const [viewType, setViewType] = useState('training');
  const [groupByFilter, setGroupByFilter] = useState('none');
  const [filterStatus, setFilterStatus] = useState(null);

  // Financial year filter state
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('ALL');
  
  // Add state for scheme and institute selection with default values
  const [selectedScheme, setSelectedScheme] = useState(initialSelectedScheme || 'ALL');
  const [selectedInstitute, setSelectedInstitute] = useState(initialSelectedInstitute);
  
  // Notification state
  const [notificationToOpen, setNotificationToOpen] = useState(null);
  const [processingNotification, setProcessingNotification] = useState(false);
  
  // Training list state
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const [maintainViewMode, setMaintainViewMode] = useState(false);
  
  // Modal state
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  // Enhanced notification handling useEffect
  useEffect(() => {
    console.log('🔥 Dashboard checking for notification in location.state:', location.state);
    
    if (location.state?.openNotification) {
      const notification = location.state.openNotification;
      console.log('🔥 Dashboard received notification:', notification);
      
      // Set the notification for ListView to process
      setNotificationToOpen(notification);
      
      // Set the appropriate view type and mode
      setViewType(notification.type);
      setViewMode('list'); // Switch to list view to show the modal
      
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle notification processed callback
  const handleNotificationProcessed = () => {
    console.log('🔥 Dashboard: Notification processed, clearing state');
    setNotificationToOpen(null);
    setProcessingNotification(false);
  };

  // Fetch notification details and open appropriate modal
  const fetchNotificationDetails = async (notification) => {
    try {
      setProcessingNotification(true);
      console.log('🔥 Dashboard fetching full details for notification:', notification);
      
      let endpoint = '';
      let fullItemData = null;
      
      // Determine API endpoint based on notification type
      if (notification.type === 'training') {
        endpoint = `${api.BASE_URL}/project/training/list`;
      } else if (notification.type === 'infra') {
        endpoint = `${api.BASE_URL}/project/infra/list`;
      } else if (notification.type === 'admin') {
        endpoint = `${api.BASE_URL}/project/admin/list`;
      }
      
      if (endpoint) {
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': localStorage.getItem('authToken')
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (!data.error && data.data) {
            // Find the specific item by ID
            fullItemData = data.data.find(item => item.id === notification.id);
            
            if (fullItemData) {
              console.log('🔥 Found full item data:', fullItemData);
              
              // Update the notification with full data
              const enhancedNotification = {
                ...notification,
                fullData: fullItemData
              };
              
              setNotificationToOpen(enhancedNotification);
            } else {
              console.log('🔥 Item not found in API response');
            }
          }
        } else {
          console.error('🔥 API response not ok:', response.status);
        }
      }
    } catch (error) {
      console.error('🔥 Error fetching notification details:', error);
    } finally {
      setProcessingNotification(false);
    }
  };

  // Handle opening modal when training code is clicked
  const handleTrainingClick = (training) => {
    setSelectedTraining(training);
    setIsModalOpen(true);
    setActionMessage('');
  };

  // Handle approve action
  const handleApprove = () => {
    setActionMessage('Training approved successfully!');
    // Here you would typically make an API call to update the status
    setTimeout(() => {
      setIsModalOpen(false);
      setActionMessage('');
    }, 1500);
  };

  const handleViewTypeChange = (type) => {
    if (type === 'list') {
      // If switching to list view, just change the view mode
      setViewMode('list');
    } else {
      // For other view types (training/infra/admin), set the view type and switch to list
      setViewType(type);
      setViewMode('list');
    }
  };

  // Add scheme selection handler
  const handleSchemeSelect = (scheme) => {
    console.log('Scheme selected:', scheme);
    setSelectedScheme(scheme);
    if (scheme !== 'ALL') {
      setSelectedInstitute(null); // Clear institute selection only when specific scheme is selected
    }
    // Stay in current view mode
    setViewType('training'); // Default to training view
  };

  // Add institute selection handler
  const handleInstituteSelect = (institute) => {
    console.log('Institute selected:', institute);
    setSelectedInstitute(institute);
    if (institute !== 'ALL') {
      setSelectedScheme(null); // Clear scheme selection only when specific institute is selected
    }
    // Stay in current view mode
    setViewType('training'); // Default to training view
  };

  // Add this function to handle refreshes from modals
  const refreshDataAndMaintainView = async () => {
    setMaintainViewMode(true);
    await fetchTrainings();
    // Reset the flag after a short delay
    setTimeout(() => {
      setMaintainViewMode(false);
    }, 100);
  };

  // Handle reject action
  const handleReject = () => {
    setActionMessage('Training rejected!');
    // Here you would typically make an API call to update the status
    setTimeout(() => {
      setIsModalOpen(false);
      setActionMessage('');
    }, 1500);
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTraining(null);
    setActionMessage('');
  };

  // Fetch trainings on component mount
  useEffect(() => {
    fetchTrainings();
  }, []);

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      
      const response = await api.training.getTrainingList();
      
      if (response && !response.error) {
        // Process the training data to ensure all items have a status
        const processedData = (response.data || []).map(training => ({
          ...training,
          status: training.status || 'Awaiting Approval' // Default status if not provided
        }));
        setTrainings(processedData);
        
        // Set the user name if available in the response
        if (response.user && response.user.name) {
          setUserName(response.user.name);
        }
        
        // If we have a pending notification and just loaded data, try to process it
        if (notificationToOpen && !notificationToOpen.fullData) {
          await fetchNotificationDetails(notificationToOpen);
        }
        
      } else {
        setError(response.message || 'Failed to fetch trainings');
      }
    } catch (err) {
      setError('Error fetching trainings: ' + (err.message || 'Unknown error'));
      console.error('Error fetching trainings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle previous month
  const handlePrevMonth = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() - 1);
      return newDate;
    });
  };

  // Handle next month
  const handleNextMonth = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(prevDate.getMonth() + 1);
      return newDate;
    });
  };

  // Format month name for calendar view
  const formatMonth = (date) => {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };
  /*
  // Build the list of financial years available in the data (normalising
  // formats like "25s26" to "25-26"), with a sensible fallback list.
  const financialYearOptions = (() => {
    const years = new Set();
    trainings.forEach(t => {
      if (t.year) years.add(String(t.year).replace('s', '-'));
    });
    // Always make sure the recent financial years are selectable
    ['26-27', '25-26'].forEach(y => years.add(y));
    return Array.from(years).sort().reverse();
  })();

  // Apply the financial year filter before passing data to the views
  const filteredTrainings = selectedFinancialYear === 'ALL'
    ? trainings
    : trainings.filter(t => String(t.year || '').replace('s', '-') === selectedFinancialYear);
*/

const normalizeFinancialYear = (value) => {
  const rawValue = String(value || '').trim();
  const digits = rawValue.replace(/\D/g, '');

  if (digits.length === 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}`;
  }

  return rawValue;
};

// Build financial-year dropdown options.
const financialYearOptions = (() => {
  const years = new Set();

  trainings.forEach((training) => {
    const normalizedYear = normalizeFinancialYear(
      training.year
    );

    if (normalizedYear) {
      years.add(normalizedYear);
    }
  });

  // Always show these financial years.
  ['26-27', '25-26'].forEach((year) => {
    years.add(year);
  });

  return Array.from(years)
    .filter(Boolean)
    .sort()
    .reverse();
})();

// Filter training records by selected financial year.
const filteredTrainings =
  selectedFinancialYear === 'ALL'
    ? trainings
    : trainings.filter((training) => {
        return (
          normalizeFinancialYear(training.year) ===
          normalizeFinancialYear(
            selectedFinancialYear
          )
        );
      });
      
  return (
    <div className="space-y-4 p-2 sm:p-4 max-w-full overflow-hidden">
      {/* Header with welcome message and view tabs in a single row */}
      <div className="flex flex-row justify-between items-center w-full">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-700">
          {userName ? `Welcomes, ${userName}` : 'Welcome'}
          {/* Show current selection */}
          {processingNotification && (
            <span className="ml-2 text-sm text-blue-600">
              Processing notification...
            </span>
          )}
        </h2>
        
        {/* Financial year filter */}
        <div className="flex items-center gap-1 ml-auto mr-2 sm:mr-4">
          <Filter className="text-gray-500" size={16} />
          <label htmlFor="financialYear" className="sr-only">Financial Year</label>
          <select
            id="financialYear"
            value={selectedFinancialYear}
            onChange={(e) => setSelectedFinancialYear(e.target.value)}
            className="text-sm border border-gray-300 rounded-md py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Filter by Financial Year"
          >
            <option value="ALL">All Years</option>
            {financialYearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* View selection tabs - in the same line as welcome message */}
        <nav className="flex space-x-1 sm:space-x-4" aria-label="Tabs">
          <button
            onClick={() => setViewMode('list')}
            className={`py-2 px-2 text-sm flex items-center ${
              viewMode === 'list'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="Implementing Agencies"
          >
            <List className="sm:mr-1" size={18} />
            <span className="hidden sm:inline">Implementing Agencies</span>
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`py-2 px-2 text-sm flex items-center ${
              viewMode === 'calendar'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="Calendar View"
          >
            <Calendar className="sm:mr-1" size={18} />
            <span className="hidden sm:inline">Calendar</span>
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`py-2 px-2 text-sm flex items-center ${
              viewMode === 'grid'
                ? 'text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title="Grid View"
          >
            <Grid className="sm:mr-1" size={18} />
            <span className="hidden sm:inline">Grid</span>
          </button>
        </nav>
      </div>
      
      {/* Calendar navigation if in calendar view */}
      {viewMode === 'calendar' && (
        <div className="flex justify-end">
          <div className="flex items-center border rounded-md overflow-hidden">
            <button 
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 border-r"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 py-1 text-sm font-medium">
              {formatMonth(currentDate)}
            </span>
            <button 
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 border-l"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-b border-gray-200 w-full"></div>

      {/* Loading state with responsive sizing */}
      {loading && (
        <div className="text-center py-4 sm:py-8">
          <svg className="animate-spin h-6 w-6 sm:h-8 sm:w-8 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-gray-600">Loading trainings...</p>
        </div>
      )}

      {/* Error state with responsive sizing */}
      {error && (
        <div className="text-center py-4 sm:py-8 text-red-500">
          <svg className="h-6 w-6 sm:h-8 sm:w-8 text-red-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="mt-2">{error}</p>
          <button 
            onClick={fetchTrainings}
            className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Content view container with full width */}
      {!loading && !error && (
        <div className="w-full overflow-x-auto">
          <div className="min-w-full">
            {viewMode === 'calendar' && (
              <CalendarView
                trainings={filteredTrainings}
                currentDate={currentDate}
                userRole={userRole}
                selectedScheme={selectedScheme}
                selectedInstitute={selectedInstitute}
              />
            )}
            {viewMode === 'list' && (
              <ListView
                trainings={filteredTrainings}
                handleTrainingClick={handleTrainingClick}
                initialViewType={viewType}
                initialGroupBy={groupByFilter}
                initialFilterStatus={filterStatus}
                refreshDataAndMaintainView={refreshDataAndMaintainView}
                selectedScheme={selectedScheme}
                selectedInstitute={selectedInstitute}
                notificationToOpen={notificationToOpen}
                onNotificationProcessed={handleNotificationProcessed}
                selectedFinancialYear={selectedFinancialYear}
              />
            )}
            {viewMode === 'grid' && (
              <GridView
                trainings={filteredTrainings}
                selectedScheme={selectedScheme}
                selectedInstitute={selectedInstitute}
              />
            )}
          </div>
        </div>
      )}

      {/* Training details modal */}
      <TrainingDetailsModal
        isOpen={isModalOpen}
        training={selectedTraining}
        onClose={handleCloseModal}
        onApprove={handleApprove}
        onReject={handleReject}
        actionMessage={actionMessage}
        fetchTrainings={fetchTrainings}
      />
    </div>
  );
};

export default Dashboard;